import { Fragment, useState, useEffect, useCallback } from "react";
import { Card, Form, Button, Table, Badge, Spinner, Modal, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import api from "../../../api";
import {
 createProgressPlan,
 getProgressPlans,
 deleteProgressPlan,
 updateProgressPlan,
} from "../../../services/progressService";
import { useSelector } from "react-redux";

/* Excel template headers */
const TEMPLATE_HEADERS = [
 "BOQ Item ID",
 "BOQ Item Name",
 "Activity Description",
 "Planned Quantity",
 "Actual Completed",
 "Unit",
 "Planned Start Date (DD/MM/YYYY)",
 "Planned End Date (DD/MM/YYYY)",
];

const downloadPlanTemplate = (rows = [], fileName = "Progress_Plan_Template.xlsx") => {
 const templateRows = rows.length
 ? rows
 : [
 ["WLI-001", "Excavation", "Site Excavation", 500, 0, "m3", "01/07/2025", "15/07/2025"],
 ["WLI-002", "Foundation", "RCC Foundation Work", 200, 0, "m3", "16/07/2025", "31/07/2025"],
 ];

 const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...templateRows]);
 ws["!cols"] = TEMPLATE_HEADERS.map(() => ({ wch: 28 }));
 const wb = XLSX.utils.book_new();
 XLSX.utils.book_append_sheet(wb, ws, "Progress Plan");
 XLSX.writeFile(wb, fileName);
};

const parseDate = (val) => {
 if (val == null || val === "") return "";
 if (typeof val === "number") {
 const jsDate = new Date(Math.round((val - 25569) * 86400 * 1000));
 return jsDate.toISOString().split("T")[0];
 }
 const s = String(val).trim();
 if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.split("T")[0];
 const parts = s.split("/");
 if (parts.length === 3 && parts[2].length === 4)
 return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
 const dash = s.split("-");
 if (dash.length === 3 && dash[2].length === 4)
 return `${dash[2]}-${dash[1].padStart(2, "0")}-${dash[0].padStart(2, "0")}`;
 return s;
};

/* */
const PhysicalProgressPlan = () => {
 const user = useSelector((s) => s.auth?.userDetails || {});
 const isAdmin = (user.role?.name || user.roleName || "").toLowerCase() === "admin";
 const canManage = isAdmin || (user.permissionNames || []).some((p) =>
 ["edit:dprs", "edit:progress", "create:dprs", "create:progress"].includes(p.toLowerCase())
 );

 const [workOrders, setWorkOrders] = useState([]);
 const [selectedWO, setSelectedWO] = useState("");
 const [plans, setPlans] = useState([]);
 const [loading, setLoading] = useState(false);
 const [uploading, setUploading] = useState(false);
 const [previewRows, setPreviewRows] = useState([]);
 const [showPreview, setShowPreview] = useState(false);
 const [editRow, setEditRow] = useState(null);
 const [showEditModal, setShowEditModal] = useState(false);

 const fetchWorkOrders = useCallback(async () => {
 try {
 const res = await api.get("/work-orders?limit=1000");
 if (res.data?.success) setWorkOrders(res.data.data);
 } catch { /* silent */ }
 }, []);

 const fetchPlans = useCallback(async () => {
 if (!selectedWO) { setPlans([]); return; }
 setLoading(true);
 try {
 const res = await getProgressPlans(selectedWO);
 setPlans(res.data?.success ? res.data.data || [] : []);
 } catch (err) {
 if (err.response?.status !== 404 && err.response?.status !== 403)
 swal("Error", "Failed to load plans", "error");
 setPlans([]);
 } finally { setLoading(false); }
 }, [selectedWO]);

 useEffect(() => { fetchWorkOrders(); }, [fetchWorkOrders]);
 useEffect(() => { fetchPlans(); }, [fetchPlans]);

 const getBoqTemplateRows = async () => {
 if (!selectedWO) return null;

 const res = await api.get(`/work-orders/${selectedWO}`);
 const workOrder = res.data?.data;
 const boqItems = workOrder?.boqItems || [];

 return {
 workOrder,
 rows: boqItems.map((item) => [
 item.wlino || item._id || "",
 item.itemName || item.description || "",
 item.itemName || item.description || "",
 Number(item.quantity || 0),
 0,
 item.unit || "",
 "",
 "",
 ]),
 };
 };

 const handleDownloadTemplate = async () => {
 if (!selectedWO) {
 downloadPlanTemplate();
 return;
 }

 setLoading(true);
 try {
 const result = await getBoqTemplateRows();
 if (!result?.rows?.length) {
 swal("Warning", "No BOQ items found. Downloading sample template.", "warning");
 downloadPlanTemplate();
 return;
 }

 downloadPlanTemplate(
 result.rows,
 `Progress_Plan_${result.workOrder?.woNo || "WO"}.xlsx`
 );
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to fetch BOQ items. Downloading sample template.", "error");
 downloadPlanTemplate();
 } finally {
 setLoading(false);
 }
 };

 /* File parse */
 const handleFileUpload = (e) => {
 const file = e.target.files[0];
 e.target.value = "";
 if (!file) return;
 if (!selectedWO) { swal("Warning", "Select a Work Order first", "warning"); return; }

 const reader = new FileReader();
 reader.onerror = () => swal("Error", "Could not read file", "error");
 reader.onload = (evt) => {
 try {
 const data = new Uint8Array(evt.target.result);
 const wb = XLSX.read(data, { type: "array", cellDates: true });
 const ws = wb.Sheets[wb.SheetNames[0]];
 const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, dateNF: "yyyy-mm-dd" });

 const parsed = rows.slice(1)
 .filter((r) => Array.isArray(r) && r[1])
 .map((r) => ({
 boqItemId: String(r[0] || "").trim(),
 boqItemName: String(r[1] || "").trim(),
 activityDescription: String(r[2] || "").trim(),
 plannedQuantity: Number(String(r[3] || "0").replace(/[^0-9.]/g, "")) || 0,
 actualQuantity: Number(String(r[4] || "0").replace(/[^0-9.]/g, "")) || 0,
 unit: String(r[5] || "").trim(),
 plannedStartDate: parseDate(r[6]),
 plannedEndDate: parseDate(r[7]),
 }));

 if (!parsed.length) {
 swal("Warning", "No data rows found. Ensure row 1 is header and data starts from row 2.", "warning");
 return;
 }
 setPreviewRows(parsed);
 setShowPreview(true);
 } catch (err) {
 console.error(err);
 swal("Error", "Failed to read Excel. Please use the downloaded template.", "error");
 }
 };
 reader.readAsArrayBuffer(file);
 };

 /* Confirm upload */
 const handleConfirmUpload = async () => {
 setUploading(true);
 try {
 const res = await createProgressPlan({ workOrder: selectedWO, items: previewRows });
 if (res.data?.success) {
 swal("Success", `${previewRows.length} activities uploaded!`, "success");
 setShowPreview(false);
 setPreviewRows([]);
 fetchPlans();
 }
 } catch (err) {
 swal("Error", err.response?.data?.message || "Upload failed", "error");
 } finally { setUploading(false); }
 };

 /* Delete */
 const handleDelete = async (id) => {
 const ok = await swal({ title: "Delete this activity?", icon: "warning", buttons: true, dangerMode: true });
 if (!ok) return;
 try {
 await deleteProgressPlan(id);
 swal("Deleted", "Activity removed", "success");
 fetchPlans();
 } catch { swal("Error", "Failed to delete", "error"); }
 };

 /* Edit save */
 const handleEditSave = async () => {
 try {
 await updateProgressPlan(editRow._id, {
 activityDescription: editRow.activityDescription,
 plannedQuantity: editRow.plannedQuantity,
 actualQuantity: editRow.actualQuantity,
 unit: editRow.unit,
 plannedStartDate: editRow.plannedStartDate,
 plannedEndDate: editRow.plannedEndDate,
 });
 swal("Saved", "Activity updated", "success");
 setShowEditModal(false);
 fetchPlans();
 } catch { swal("Error", "Failed to update", "error"); }
 };

 const selectedWOObj = workOrders.find((w) => w._id === selectedWO);

 /* RENDER */
 return (
 <Fragment>
 {/* Page Header */}
 <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
 <div>
 <h4 className="fw-bold mb-1">
 <i className="fas fa-chart-line text-primary me-2" />
 Physical Progress Plan
 </h4>
 <p className="text-muted mb-0 small">
 Upload planned and actual progress in the same Excel template.
 </p>
 </div>
 <div className="d-flex gap-2">
 <Button variant="outline-secondary" size="sm" onClick={handleDownloadTemplate}>
 <i className="fas fa-download me-1" /> Download Template
 </Button>
 <Link to="/progress/report" className="btn btn-primary btn-sm">
 <i className="fas fa-chart-bar me-1" /> View Progress Report
 </Link>
 </div>
 </div>

 {/* HOW IT WORKS strip */}
 <div className="d-flex gap-0 mb-4 rounded-3 overflow-hidden border" style={{ fontSize: 13 }}>
 {[
 { step: "1", icon: "fa-download", color: "#0078D7", label: "Download Template", sub: "Get the Excel format" },
 { step: "2", icon: "fa-file-excel", color: "#28A745", label: "Fill Template", sub: "Add plan, actual & dates" },
 { step: "3", icon: "fa-upload", color: "#F47920", label: "Upload Here", sub: "Select work order & upload" },
 { step: "4", icon: "fa-chart-bar", color: "#6F42C1", label: "View Report", sub: "See Planned vs Actual" },
 ].map((s, i) => (
 <div
 key={i}
 className="flex-fill d-flex align-items-center gap-3 px-3 py-3"
 style={{ background: i % 2 === 0 ? "#f8f9fa" : "#fff", borderRight: i < 3 ? "1px solid #dee2e6" : "none" }}
 >
 <div
 className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold"
 style={{ width: 38, height: 38, background: s.color, fontSize: 13 }}
 >
 {s.step}
 </div>
 <div>
 <div className="fw-semibold">{s.label}</div>
 <div className="text-muted" style={{ fontSize: 11 }}>{s.sub}</div>
 </div>
 </div>
 ))}
 </div>

 {/* Step 3 Card: Select WO + Upload */}
 <Card className="mb-4 border-0 shadow-sm">
 <Card.Body className="p-4">
 <p className="fw-semibold mb-3 text-primary">
 <i className="fas fa-upload me-2" />
 Step 3 — Select Work Order &amp; Upload Plan
 </p>

 <Row className="g-3 align-items-end">
 {/* Work Order */}
 <Col md={5}>
 <Form.Label className="fw-semibold small mb-1">
 Work Order <span className="text-danger">*</span>
 </Form.Label>
 <Form.Select
 value={selectedWO}
 onChange={(e) => setSelectedWO(e.target.value)}
 style={{ height: 42 }}
 >
 <option value="">-- Select Work Order --</option>
 {workOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo}{wo.label ? ` – ${wo.label}` : ""} — {wo.project?.projectName}
 </option>
 ))}
 </Form.Select>
 </Col>

 {/* Upload — only if can manage */}
 {canManage && (
 <Col md={5}>
 <Form.Label className="fw-semibold small mb-1">
 Upload Excel File
 {!selectedWO && <span className="text-muted ms-2" style={{ fontSize: 11 }}>(select work order first)</span>}
 </Form.Label>
 <Form.Control
 type="file"
 accept=".xlsx,.xls"
 onChange={handleFileUpload}
 disabled={!selectedWO}
 style={{ height: 42 }}
 />
 </Col>
 )}

 {/* Info badge */}
 <Col md={2} className="d-flex align-items-end pb-1">
 {selectedWO && selectedWOObj && (
 <Badge bg="light" text="dark" className="border px-3 py-2" style={{ fontSize: 11 }}>
 <i className="fas fa-check-circle text-success me-1" />
 WO Selected
 </Badge>
 )}
 </Col>
 </Row>
 </Card.Body>
 </Card>

 {/* Plan Table */}
 <Card className="border-0 shadow-sm">
 <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
 <div>
 <span className="fw-bold">
 Uploaded Plan
 {selectedWOObj && (
 <span className="text-muted fw-normal ms-2" style={{ fontSize: 13 }}>
 — {selectedWOObj.woNo} · {selectedWOObj.project?.projectName}
 </span>
 )}
 </span>
 </div>
 {plans.length > 0 && (
 <Badge bg="primary" className="rounded-pill px-3">{plans.length} activities</Badge>
 )}
 </Card.Header>

 <Card.Body className="p-0">
 {!selectedWO ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-hand-point-up fs-2 mb-3 d-block text-primary opacity-50" />
 <h6 className="fw-semibold">Select a Work Order above</h6>
 <p className="small mb-0">Your uploaded plan will appear here</p>
 </div>
 ) : loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" variant="primary" size="sm" />
 <p className="text-muted small mt-2 mb-0">Loading plan…</p>
 </div>
 ) : plans.length === 0 ? (
 <div className="text-center py-5 text-muted">
 <i className="fas fa-file-excel fs-2 mb-3 d-block text-success opacity-50" />
 <h6 className="fw-semibold">No plan uploaded yet</h6>
 <p className="small mb-3">Download the template, fill your planned activities &amp; dates, then upload above.</p>
 <Button variant="outline-secondary" size="sm" onClick={handleDownloadTemplate}>
 <i className="fas fa-download me-1" /> Download Template
 </Button>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light">
 <tr>
 <th className="ps-3">#</th>
 <th>BOQ ID</th>
 <th>BOQ Item</th>
 <th>Activity</th>
 <th className="text-end">Planned Qty</th>
 <th className="text-end">Actual</th>
 <th>Unit</th>
 <th>Start Date</th>
 <th>End Date</th>
 <th>Status</th>
 {canManage && <th className="text-center">Actions</th>}
 </tr>
 </thead>
 <tbody>
 {plans.map((row, idx) => {
 const isOverdue = row.plannedEndDate && new Date(row.plannedEndDate) < new Date();
 return (
 <tr key={row._id}>
 <td className="ps-3 text-muted">{idx + 1}</td>
 <td><code className="text-primary">{row.boqItemId || "—"}</code></td>
 <td className="fw-semibold">{row.boqItemName}</td>
 <td className="text-muted">{row.activityDescription || "—"}</td>
 <td className="text-end fw-bold">{row.plannedQuantity}</td>
 <td className="text-end fw-bold text-success">{row.actualQuantity || 0}</td>
 <td>{row.unit}</td>
 <td>{row.plannedStartDate ? new Date(row.plannedStartDate).toLocaleDateString("en-IN") : "—"}</td>
 <td>
 {row.plannedEndDate ? (
 <span className={isOverdue ? "text-danger fw-semibold" : ""}>
 {new Date(row.plannedEndDate).toLocaleDateString("en-IN")}
 </span>
 ) : "—"}
 </td>
 <td>
 <Badge bg={isOverdue ? "danger" : "success"} style={{ fontSize: 10 }}>
 {isOverdue ? "Overdue" : "Active"}
 </Badge>
 </td>
 {canManage && (
 <td className="text-center">
 <Button
 variant="outline-primary"
 size="sm"
 className="me-1 px-2"
 onClick={() => { setEditRow({ ...row }); setShowEditModal(true); }}
 >
 <i className="fas fa-edit" />
 </Button>
 <Button
 variant="outline-danger"
 size="sm"
 className="px-2"
 onClick={() => handleDelete(row._id)}
 >
 <i className="fas fa-trash" />
 </Button>
 </td>
 )}
 </tr>
 );
 })}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 {/* Preview Modal */}
 <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" centered>
 <Modal.Header closeButton className="border-bottom">
 <Modal.Title style={{ fontSize: 16 }}>
 <i className="fas fa-eye text-primary me-2" />
 Review before uploading — <strong>{previewRows.length} activities</strong>
 </Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-0" style={{ maxHeight: "60vh", overflowY: "auto" }}>
 <Table bordered hover size="sm" className="mb-0 align-middle" style={{ fontSize: 13 }}>
 <thead className="table-light sticky-top">
 <tr>
 <th className="ps-3">#</th>
 <th>BOQ ID</th>
 <th>BOQ Item Name</th>
 <th>Activity</th>
 <th className="text-end">Planned Qty</th>
 <th className="text-end">Actual</th>
 <th>Unit</th>
 <th>Start Date</th>
 <th>End Date</th>
 </tr>
 </thead>
 <tbody>
 {previewRows.map((r, i) => (
 <tr key={i}>
 <td className="ps-3 text-muted">{i + 1}</td>
 <td><code>{r.boqItemId || "—"}</code></td>
 <td className="fw-semibold">{r.boqItemName}</td>
 <td className="text-muted">{r.activityDescription || "—"}</td>
 <td className="text-end fw-bold">{r.plannedQuantity}</td>
 <td className="text-end fw-bold text-success">{r.actualQuantity || 0}</td>
 <td>{r.unit}</td>
 <td>{r.plannedStartDate || "—"}</td>
 <td>{r.plannedEndDate || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Modal.Body>
 <Modal.Footer className="border-top bg-light">
 <span className="text-muted small me-auto">
 <i className="fas fa-info-circle text-primary me-1" />
 Review the data above. Click "Upload Now" to save it.
 </span>
 <Button variant="outline-secondary" onClick={() => setShowPreview(false)}>
 Cancel
 </Button>
 <Button variant="primary" onClick={handleConfirmUpload} disabled={uploading}>
 {uploading ? (
 <><Spinner size="sm" className="me-2" />Uploading…</>
 ) : (
 <><i className="fas fa-upload me-1" />Upload Now</>
 )}
 </Button>
 </Modal.Footer>
 </Modal>

 {/* Edit Modal */}
 <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
 <Modal.Header closeButton>
 <Modal.Title style={{ fontSize: 16 }}>Edit Activity</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {editRow && (
 <Form>
 <Form.Group className="mb-3">
 <Form.Label className="small fw-semibold">Activity Description</Form.Label>
 <Form.Control
 value={editRow.activityDescription}
 onChange={(e) => setEditRow((p) => ({ ...p, activityDescription: e.target.value }))}
 />
 </Form.Group>
 <Row className="g-2 mb-3">
 <Col>
 <Form.Label className="small fw-semibold">Planned Quantity</Form.Label>
 <Form.Control
 type="number"
 value={editRow.plannedQuantity}
 onChange={(e) => setEditRow((p) => ({ ...p, plannedQuantity: e.target.value }))}
 />
 </Col>
 <Col>
 <Form.Label className="small fw-semibold">Actual Completed</Form.Label>
 <Form.Control
 type="number"
 value={editRow.actualQuantity || 0}
 onChange={(e) => setEditRow((p) => ({ ...p, actualQuantity: e.target.value }))}
 />
 </Col>
 <Col>
 <Form.Label className="small fw-semibold">Unit</Form.Label>
 <Form.Control
 value={editRow.unit}
 onChange={(e) => setEditRow((p) => ({ ...p, unit: e.target.value }))}
 />
 </Col>
 </Row>
 <Row className="g-2">
 <Col>
 <Form.Label className="small fw-semibold">Start Date</Form.Label>
 <Form.Control
 type="date"
 value={editRow.plannedStartDate?.split("T")[0] || ""}
 onChange={(e) => setEditRow((p) => ({ ...p, plannedStartDate: e.target.value }))}
 />
 </Col>
 <Col>
 <Form.Label className="small fw-semibold">End Date</Form.Label>
 <Form.Control
 type="date"
 value={editRow.plannedEndDate?.split("T")[0] || ""}
 onChange={(e) => setEditRow((p) => ({ ...p, plannedEndDate: e.target.value }))}
 />
 </Col>
 </Row>
 </Form>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={handleEditSave}>
 <i className="fas fa-save me-1" /> Save Changes
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default PhysicalProgressPlan;
